'use client';

import { useCallback } from 'react';
import useSWR from 'swr';
import { myVisitCounselApi } from '@/entities/planner/model/my-planner.api';
import { orderApi } from '@/entities/order';
import { customerApi } from '@/entities/customer';
import type { VisitCounselView, VisitCounselEventView } from './visit-counsel.types';
import type { OrderView } from '@/entities/order';
import type { CustomerView } from '@/entities/customer';

export interface MyVisitCounselDetailData {
  counsel: VisitCounselView;
  order: OrderView | null;
  events: VisitCounselEventView;
  customer: CustomerView | null;
}

async function fetchDetail(_key: readonly [string, string]): Promise<MyVisitCounselDetailData> {
  const [, visitCounselId] = _key;
  const [counsel, events] = await Promise.all([
    myVisitCounselApi.getMyVisitCounselById(visitCounselId),
    myVisitCounselApi.getMyVisitCounselEvents(visitCounselId),
  ]);

  let order: OrderView | null = null;
  try {
    order = await orderApi.getOrder(counsel.orderId);
  } catch {
    // order 조회 실패 시 null 유지
  }

  let customer: CustomerView | null = null;
  if (order?.ordererMisoCustomerId) {
    try {
      customer = await customerApi.getCustomerByMisoId(order.ordererMisoCustomerId);
    } catch {
      // customer 조회 실패 시 null 유지
    }
  }

  return { counsel, order, events, customer };
}

export interface UseMyVisitCounselDetailReturn {
  data: MyVisitCounselDetailData | null;
  isLoading: boolean;
  /** 백그라운드 재검증 중 (캐시는 이전 값일 수 있음) */
  isValidating: boolean;
  error: Error | null;
  refetch: () => Promise<MyVisitCounselDetailData | undefined>;
  /** API 응답으로 counsel만 즉시 갱신 (삭제/추가 후 동기화용) */
  mutateWithCounsel: (counsel: VisitCounselView) => void;
}

/**
 * PLANNER 전용: 본인에게 배정된 방문상담 상세
 * - GET /api/v1/visit-counsels/:id
 * - GET /api/v1/visit-counsels/:id/events
 * - GET /api/v1/orders/:orderId (order join)
 */
export function useMyVisitCounselDetail(
  visitCounselId: string | null,
  enabled: boolean = true
): UseMyVisitCounselDetailReturn {
  const { data, error, isLoading, isValidating, mutate } = useSWR<MyVisitCounselDetailData>(
    enabled && visitCounselId ? (['my-visit-counsel-detail', visitCounselId] as const) : null,
    fetchDetail,
    {
      revalidateOnFocus: false,
      // 전역 revalidateOnMount: false 를 개별 override —
      // SWR v2는 캐시가 없어도 revalidateOnMount:false면 초기 fetch를 건너뜀
      revalidateOnMount: true,
    }
  );

  const mutateWithCounsel = useCallback(
    (counsel: VisitCounselView) => {
      mutate((prev) => (prev ? { ...prev, counsel } : prev), { revalidate: false });
    },
    [mutate]
  );

  const refetch = useCallback(
    () => mutate() as Promise<MyVisitCounselDetailData | undefined>,
    [mutate]
  );

  return {
    data: data ?? null,
    isLoading,
    isValidating,
    error: error ? (error instanceof Error ? error : new Error('상담 상세 조회 실패')) : null,
    refetch,
    mutateWithCounsel,
  };
}
